import { createSlice, createAsyncThunk, createEntityAdapter } from "@reduxjs/toolkit";
import { request } from "@/utils/api";

const codRemittanceAdapter = createEntityAdapter({
    selectId: (remittance) => remittance._id, // Assuming each remittance has an _id field
    sortComparer: (a, b) => new Date(b.createdAt) - new Date(a.createdAt) // Sort by date descending
});
export const fetchCODRemittances = createAsyncThunk(
    "codRemittance/fetchAll",
    async ({ startDate, endDate }, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: `/admin/getCodRemittance`,
                method: "GET",
                params: { startDate, endDate },
            });
            if (!response.success || !Array.isArray(response.data?.codRemittances)) {
                return rejectWithValue(response.error.message || "Failed to fetch COD remittances");
            }
            return response.data;

        } catch ({ error }) {
            console.error("Error fetching COD remittances:", error.message);
            return rejectWithValue(error.message || "Failed to fetch COD remittances");
        }
    }
)
export const uploadCODRemittance = createAsyncThunk(
    "codRemittance/upload",
    async ({ file }, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            formData.append("file", file);

            const response = await request({
                endpoint: "/admin/addCourierCODPayment",
                method: "POST",
                body: formData,
                headers: { "Content-Type": "multipart/form-data" },
            });

            if (!response.success) {
                return rejectWithValue(response.error.message || "Failed to upload COD remittance");
            }

            return response.data; // Return relevant response data
        } catch ({ error }) {
            return rejectWithValue(error.message || "Failed to upload COD remittance");
        }
    }
);
export const customerPaymentForm = createAsyncThunk(
    "customerPayment/upload",
    async ({ file }, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            formData.append("file", file);

            const response = await request({
                endpoint: "/admin/makeCustomerPayment",
                method: "POST",
                body: formData,
                headers: { "Content-Type": "multipart/form-data" },
            });

            if (!response.success || !Array.isArray(response.data)) {
                return rejectWithValue(response.error.message || "Failed to upload COD remittance");
            }

            return response.data; // Return relevant response data
        } catch ({ error }) {
            return rejectWithValue(error.message || "Failed to upload COD remittance");
        }
    }
);

const codRemittanceSlice = createSlice({
    name: "codRemittance",
    initialState: codRemittanceAdapter.getInitialState({
        totalDueFromCourier: null,
        totalReceiveFromCourier: null,
        totalCodAmountGivenToCustomer: null,
        totalCodAmountDueToCustomer: null,
        loading: false,
        error: null,
    }),
    reducers: {
        addCodRemittance: (state, action) => {
            codRemittanceAdapter.setAll(state, action.payload.remittance);
            state.totalDue = action.payload.totalDue;
            state.totalRemittance = action.payload.totalRemittance;
            state.lastRemittance = action.payload.lastRemittance;
        },
        clearCodRemittance: (state) => {
            codRemittanceAdapter.removeAll(state);
            state.totalDue = null;
            state.totalRemittance = null;
            state.lastRemittance = null;
            state.loading = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(uploadCODRemittance.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(uploadCODRemittance.fulfilled, (state, action) => {
                console.log("COD Remittance Response:", action.payload); // Debugging
                state.loading = false;
                codRemittanceAdapter.upsertMany(state, action.payload);
            })
            .addCase(uploadCODRemittance.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            //customer
            .addCase(customerPaymentForm.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(customerPaymentForm.fulfilled, (state, action) => {
                console.log("COD Remittance Response:", action.payload); // Debugging
                state.loading = false;
                codRemittanceAdapter.upsertMany(state, action.payload);
            })
            .addCase(customerPaymentForm.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            //fetch all
            .addCase(fetchCODRemittances.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCODRemittances.fulfilled, (state, action) => {
                state.loading = false;
                codRemittanceAdapter.setAll(state, action.payload.codRemittances);
                state.totalDueFromCourier = action.payload.totalDueFromCourier;
                state.totalReceiveFromCourier = action.payload.totalReceiveFromCourier;
                state.totalCodAmountGivenToCustomer = action.payload.totalCodAmountGivenToCustomer;
                state.totalCodAmountDueToCustomer = action.payload.totalCodAmountDueToCustomer;
            })
            .addCase(fetchCODRemittances.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            ;
    },
});
export const { clearCodRemittance, addCodRemittance } = codRemittanceSlice.actions;
export default codRemittanceSlice.reducer;
export const {
    selectAll: selectAllCODRemittances,
    selectById: selectCODRemittanceById,
    selectIds: selectCODRemittanceIds,
} = codRemittanceAdapter.getSelectors((state) => state.codRemittance);
